import './utils/zdate';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from "react-router-dom";
import { alpha, createTheme, ThemeProvider } from '@mui/material/styles';
import App from '@plannel/App';
import reportWebVitals from '@plannel/reportWebVitals';
import { GlobalStyles } from '@mui/material';

import '@plannel/utils/i18n'

import { store } from '@plannel/redux/modules/store';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';

import { ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';

import { grey } from '@mui/material/colors';
import { FS } from '@plannel/utils/font-util';
/*
 * Claude Design "PlanNEL Side Panels"(1b) 가 정한 중립 팔레트. 사이드 패널이 원 소비처지만
 * 같은 시안의 date picker(턴 2/2a)도 같은 색을 쓰므로 값을 복사하지 않고 그 정의를 그대로 참조한다
 * — 팔레트를 되돌릴 때 고칠 곳이 한 군데로 남아야 한다.
 */
import { PANEL } from '@plannel/components/ui/SidePanel';
LicenseManager.setLicenseKey("Using_this_{AG_Grid}_Enterprise_key_{AG-095549}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{zionex}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{16_January_2027}____[v3]_[01]_MTgwMDA1NzYwMDAwMA==0d170af84196c8af7a385ba7ef306b6d");

// 통합 차트/스파크라인(ag-charts)을 안 쓴다 — 그리드 셀 막대는 components/aggrid/RatioBarCell(DOM).
ModuleRegistry.registerModules([
  AllEnterpriseModule,
]);

export function Styles() {
  return (
      <GlobalStyles
          styles={{
            // Only disable when ModalManager injects inline padding-right
            'body[style*="padding-right"]': {
              paddingRight: '0 !important',
            },
          }}
      />
  );
}

/*
 * 컨트롤(버튼·아이콘버튼·입력칸·토글) 공통 모서리 반경.
 *   MUI 기본값 4px 는 이미 8px 로 맞춰 둔 팝업 셸(Popover/Menu/Autocomplete paper)보다 각져서
 *   같은 툴바 안에서 두 규격이 어긋나 보였다. 팝업과 같은 8px 로 통일한다
 *   (알약이어야 하는 Chip·SegmentedControl·StatPill 등은 자체 sx 로 자기 값을 유지한다).
 */
const CONTROL_RADIUS = 8;

/*
 * 캘린더 월/연 뷰의 한 칸. 두 그리드가 규격이 같아 한 곳에서 만든다.
 * (styleOverrides 콜백이 넘겨주는 theme 을 받는다 — 아래 MuiPickersMonth/MuiPickersYear 참고)
 */
const PICKER_CELL_SX = (theme) => ({
  width: 'calc(100% - 10px)',   // 칸(1/3) 을 꽉 채우고 좌우 5px 만 남긴다
  height: 40,
  borderRadius: 12,
  fontSize: FS.TITLE,
  fontWeight: 400,
  color: PANEL.inkBody,
  transition: 'background-color .15s ease, color .15s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: PANEL.accentDeep,
  },
  '&:focus': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
  '&.Mui-selected': {
    fontWeight: 500,
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.35)}`,
    '&:hover, &:focus': { backgroundColor: theme.palette.primary.dark },
  },
  '&.Mui-disabled': { color: PANEL.iconFaint },
});

const THEME = createTheme({
  typography: {
    // 실제 스택은 언어에 따라 달라진다 — utils/font-util.js 참고
    "fontFamily": 'var(--app-font-family)',
    "fontSize": 14,
    "fontWeightLight": 300,
    "fontWeightRegular": 400,
    "fontWeightMedium": 500,
    "fontWeightBold": 700
  },
  palette: {
    primary: {
      light: '#06add3',
      main: '#048BA8',
      dark: '#025B6E',
      contrastText: '#fff',
    },
    black: {
      main: grey[900]
    }
  },
  components: {
    /*
     * MUI variant 기본 크기 → 폰트 토큰.
     *   `variant="h6"` 처럼 fontSize 없이 쓰는 자리가 약 250곳이라, 호출부를 고치는 대신
     *   여기 한 곳에서 덮는다. 매핑은 타이포 표준 §6 의 기계적 규칙(현재 px → 역할)이라
     *   넓은 화면 기준 크기가 거의 그대로 유지되고 토글만 붙는다.
     *   (개별 `sx={{ fontSize }}` 는 여기보다 우선하므로 기존 지정은 그대로 이긴다.)
     *   ※ 좁은 화면(≤1400px)에서는 App.scss 의 `!important` 규칙이 다시 눌러 준다 — 현행과 동일.
     */
    MuiTypography: {
      styleOverrides: {
        h5: { fontSize: FS.KPI },           // 24 → 24/28
        h6: { fontSize: FS.DISPLAY },       // 20 → 18/21
        subtitle1: { fontSize: FS.H1 },     // 16 → 15/17
        subtitle2: { fontSize: FS.H2 },     // 14 → 14/15
        body1: { fontSize: FS.H1 },         // 16 → 15/17
        body2: { fontSize: FS.H2 },         // 14 → 14/15
        caption: { fontSize: FS.SUB },      // 12 → 12/13
      },
    },
    /*
     * 체크박스·라디오 옆 라벨은 보조 텍스트다 — `body1`(H1, 15/18px)로 두면 컨트롤보다 커 보인다.
     *   원래 App.scss 가 좁은 화면에서만 `--fs-sub` 로 눌러주고 있었는데(넓은 화면은 16px),
     *   그 미디어 규칙을 걷어내면서 폭에 상관없이 일관되게 여기로 옮겼다.
     */
    MuiFormControlLabel: {
      styleOverrides: { label: { fontSize: FS.SUB } },   // 12/14
    },
    // 입력 라벨·입력값도 MUI 기본이 1rem(16px)이라 토글에 안 따라왔다.
    //   상단 필터 툴바의 "Version / Locations / Item …" 라벨이 여기 해당한다.
    //   (좁은 화면에서는 App.scss 가 다시 title/h2 로 눌러 준다 — 현행과 동일한 2단 구조.)
    /*
     * `FormLabel` 은 라디오/체크박스 **그룹 헤더**로 쓴다(계층 그룹 팝오버 등).
     *   MUI 기본이 `theme.typography.body1`(16px 고정)이라 아래 옵션 라벨(sub, 12/14)과 4px 벌어졌다.
     *   옵션에서 한 칸 건너뛴 h2 로 낮춘다 — §1 규칙 2(인접 역할로 위계 만들지 않기).
     *   ⚠ `InputLabel` 이 `FormLabel` 을 상속하므로, 아래 `MuiInputLabel` 오버라이드가 그 위에 얹혀야 한다.
     */
    MuiFormLabel: {
      styleOverrides: { root: { fontSize: FS.H2 } },   // 16 → 14/16
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: FS.H1 } },   // 16 → 15/17
    },
    /*
     * ⚠ `input` 이 아니라 **`root`** 에 준다.
     *   MUI 의 `.MuiInputBase-input` 은 기본이 `font: inherit` 이라 루트 크기를 따라간다.
     *   input 에 직접 박으면 그 상속 계약이 깨져서, 컴포넌트가 `<Select sx={{ fontSize: FS.TITLE }}>`
     *   처럼 루트에 준 크기가 **무시된다**(Chart Settings 의 드롭다운이 이름보다 커졌던 원인).
     *   root 에 주면 sx 가 정상적으로 이기고, input 은 그대로 상속받는다.
     */
    MuiInputBase: {
      styleOverrides: { root: { fontSize: FS.H1 } },   // 16 → 15/17
    },
    /*
     * 아웃라인 입력의 노치(테두리에 뚫리는 라벨 자리) 폭 맞추기.
     *   MUI 는 라벨을 `transform: scale(0.75)` 로 줄이고, 노치를 그리는 <legend> 에는
     *   `font-size: 0.75em` 을 준다 — 둘의 기준이 같아야 폭이 맞는다.
     *   그런데 라벨만 토큰으로 바꾸면 legend 의 em 기준은 상속된 16px 로 남아 어긋난다
     *   (실측: 노치 여유가 Small 14.5px → Big 5.5px 로 줄었다. 긴 라벨이면 테두리를 침범한다.)
     *   root 에 같은 토큰을 주면 legend 의 0.75em 이 라벨과 같은 기준으로 계산된다.
     */
    // 모서리도 함께 준다: 검색 상자·필터 툴바의 Select/Autocomplete 가 전부 outlined 라 이 한 곳이면 된다
    //   (테두리를 그리는 <fieldset>(NotchedOutline)은 `borderRadius: inherit` 이라 자동으로 따라온다).
    MuiOutlinedInput: {
      styleOverrides: { root: { fontSize: FS.H1, borderRadius: CONTROL_RADIUS } },
    },
    /*
     * ⚠ `theme.typography.*` 를 **테마 생성 시점에 펼쳐 넣는** 컴포넌트들.
     *   이들은 자기 스타일 안에 `...theme.typography.button` 같은 식으로 값을 박제하므로
     *   위의 `MuiTypography` 오버라이드가 **닿지 않는다** — 따로 지정해야 토글에 따라온다.
     *   (실측: 상단 탭 헤더가 Small/Big 양쪽에서 14px 로 고정돼 있었다.)
     *   값은 각 컴포넌트의 MUI 기본 px 를 §6 표로 매핑한 것 → Small 은 현행과 동일.
     */
    MuiTab: {
      styleOverrides: { root: { fontSize: FS.H2 } },        // 14 → 14/16
    },
    MuiAlert: {
      styleOverrides: { root: { fontSize: FS.H2 } },        // 14 → 14/16 (Snackbar 내용)
    },
    /*
     * 칩 = 배지다. **본문보다 커지면 안 된다.**
     *   MUI 기본 13px 를 그대로 두면 그리드 셀 텍스트(sub, 12/14)보다 1px 커진다
     *   — Version Management 의 상태 칩 20개가 전부 셀 안에 있어 실제로 그렇게 보였다.
     *   자체 알약인 `AbcXyzChip`(components/aggrid/AbcXyzChip.js)이 쓰는 `FS.BODY` 에 맞춰
     *   두 칩 계열의 크기를 통일한다.
     */
    MuiChip: {
      styleOverrides: { label: { fontSize: FS.BODY } },     // 13 → 11/13 (AbcXyzChip 과 동일)
    },
    MuiInputAdornment: {
      styleOverrides: { root: { fontSize: FS.H1 } },        // 16 → 15/18
    },
    // 버튼도 MUI 기본값이라 안 따라왔다. size 별로 값이 달라 root 하나로 덮으면 세 크기가 뭉개진다.
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: CONTROL_RADIUS },
        sizeSmall: { fontSize: FS.TITLE },   // 13 → 13/14
        sizeMedium: { fontSize: FS.H2 },     // 14 → 14/15
        sizeLarge: { fontSize: FS.H1 },      // 15 → 15/17
      },
    },
    /*
     * 아이콘 버튼은 MUI 기본이 `borderRadius: 50%`(원형)이라 옆에 선 사각 버튼·입력칸과 따로 논다.
     *   테두리가 없어 평소엔 티가 안 나지만 호버 배경이 원으로 뜬다 — 툴바에서 나란히 놓이는
     *   조회/엑셀/설정 아이콘이 여기 해당한다. 같은 8px 로 맞춘다.
     *   ⚠ 원형이어야 하는 자리(RoundToggleButton 등)는 자체 sx 로 50% 를 주고 있어 그대로 이긴다.
     */
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: CONTROL_RADIUS },
      },
    },
    // 토글 버튼(단독). 그룹(ToggleButtonGroup)의 안쪽 모서리 각지게 하는 규칙은 그룹 쪽 선택자가 이긴다.
    MuiToggleButton: {
      styleOverrides: {
        root: { borderRadius: CONTROL_RADIUS },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          // Always reserve scrollbar space whether scroll appears or not
          scrollbarGutter: 'stable', // or 'stable both-edges'
        },
        // Fallback for older browsers
        '@supports not (scrollbar-gutter: stable)': {
          html: {
            overflowY: 'scroll', // Always reserve scroll area
          },
        },
      },
    },
    // ── 드롭다운 팝업(열린 목록) 공통 룩 ─────────────────────────────────
    //   Select 팝업·버튼 액션 메뉴·Autocomplete 목록의 열린 상태만 통일한다.
    //   닫힌 입력 필드(높이·테두리·폰트)는 건드리지 않음.
    //   개별 화면의 sx/scss(CompactSelect, GridAutoComplete 등)가 필요 시 이 기본값을 이긴다.
    // Popover 셸(체크박스 트리 드롭다운·알림 패널 등 Menu가 아닌 팝업)도 동일한 룩 적용.
    //   내부 여백은 콘텐츠가 제각각이라 지정하지 않는다.
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          border: '0.5px solid rgba(0, 0, 0, 0.12)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: 4,
          borderRadius: 8,
          border: '0.5px solid rgba(0, 0, 0, 0.12)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        },
        list: {
          padding: '4px 6px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 6,
          fontSize: FS.TITLE,
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
            '&.Mui-focusVisible': { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
          },
        }),
      },
    },
    MuiSelect: {
      defaultProps: {
        // 지역 MenuProps 상수(maxHeight 300 패턴) 없이도 긴 목록이 스크롤되도록 기본값 제공
        MenuProps: { PaperProps: { style: { maxHeight: 320 } } },
      },
    },
    // 트리형 드롭다운(고객/아이템 계층 필터 등)의 행도 같은 호버·폰트 문법을 따른다 (@mui/x-tree-view)
    MuiTreeItem: {
      styleOverrides: {
        content: ({ theme }) => ({
          borderRadius: 6,
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
          '&.Mui-focused': { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
          '&.Mui-selected, &.Mui-selected.Mui-focused': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
          '&.Mui-selected:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
        }),
        label: {
          fontSize: FS.TITLE,
          // 체크박스 트리(FormControlLabel 라벨)는 Typography(14px)가 상속을 덮으므로 직접 지정
          '& .MuiFormControlLabel-label': { fontSize: FS.TITLE },
        },
      },
    },
    // RichTreeView 계열(TreeItem2 — Location 필터 등)도 동일 룩 적용
    MuiTreeItem2: {
      styleOverrides: {
        content: ({ theme }) => ({
          borderRadius: 6,
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
          '&.Mui-focused': { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
          '&.Mui-selected, &.Mui-selected.Mui-focused': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
          '&.Mui-selected:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
        }),
        label: {
          fontSize: FS.TITLE,
          '& .MuiFormControlLabel-label': { fontSize: FS.TITLE },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          marginTop: 4,
          borderRadius: 8,
          border: '0.5px solid rgba(0, 0, 0, 0.12)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        },
        listbox: ({ theme }) => ({
          padding: '4px 6px',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.grey[300], borderRadius: 3 },
          '& .MuiAutocomplete-option': {
            borderRadius: 6,
            fontSize: FS.TITLE,
            '&.Mui-focused': { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
            '&[aria-selected="true"]': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
            '&[aria-selected="true"].Mui-focused': { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
          },
        }),
        groupLabel: {
          fontSize: FS.BODY,
          fontWeight: 700,
          letterSpacing: 0.4,
          lineHeight: '28px',
        },
      },
    },
    // 툴팁 — 어두운 기본 대신 옅은 노랑(크림) 배경 + 진한 글자로 통일
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#FFFDF7',
          color: 'rgba(0, 0, 0, 0.82)',
          border: '1px solid #EFE9DC',
          borderRadius: 6,
          fontSize: FS.BODY,
          fontWeight: 400,
          padding: '5px 9px',
          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.12)',
        },
        arrow: {
          color: '#FFFDF7',
          '&::before': { border: '1px solid #EFE9DC' },
        },
      },
    },
    /* ── 날짜 선택 캘린더(@mui/x-date-pickers) ──────────────────────────────
     *
     * Claude Design "PlanNEL Side Panels" 턴 2 의 **2a(pill selection)** 안을 적용한다.
     * 시안 자체가 "1b 언어(= components/ui/SidePanel.js)로 다시 그린 date picker"라
     * 중립 색(잉크·라벨·주말·아이콘)은 그 PANEL 팔레트를 그대로 재사용한다.
     *
     * 두 가지는 시안을 따르지 않는다. 둘 다 "앱 전체 어휘 > 이 화면 하나" 판단이다.
     *  ① **팝오버 셸**은 시안의 radius 14 + `0 10px 32px`(패널 카드) 대신
     *     위 드롭다운 규약(8px + 헤어라인 + `0 8px 24px`)을 유지한다. 같은 필터 툴바에서
     *     Select 팝업과 캘린더가 서로 다른 카드처럼 보이면 통일해 둔 팝업 룩이 다시 갈라진다.
     *     → 여기서 바꾸는 것은 **셸 안쪽 레이아웃뿐**이다.
     *  ② **선택 색**은 시안의 #12a3c6 대신 앱 `primary.main`(#048BA8)을 쓴다.
     *     시안 주석도 "선택은 지금 쓰는 청록 알약을 유지하고 글로우만 얹는다"라고 적고 있고,
     *     DatePicker 는 291곳에서 쓰는 공통 컨트롤이라 선택 상태만 다른 색이면 안 된다
     *     (흰 글자 대비도 #12a3c6(2.97:1)보다 primary(3.99:1)가 낫다).
     *
     * 크기는 시안의 절대값(10 / 12.5 / 13 / 14px) 대신 FS 토큰으로 옮겼다 — SidePanel 과 같은 이유로
     * Big/Small 토글이 살아 있어야 한다. 폭(320px)·주 단위 높이 등 MUI 의 고정 치수는 건드리지 않는다.
     */
    MuiPickersPopper: {
      styleOverrides: {
        paper: {
          marginTop: 4,
          borderRadius: 8,
          border: '0.5px solid rgba(0, 0, 0, 0.12)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiDateCalendar: {
      // MUI 기본 높이(336)는 App.scss 가 이미 auto 로 풀어 놨다. 아래 여백만 시안만큼 준다.
      styleOverrides: { root: { paddingBottom: 6 } },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        // 좌측 여백은 아래 요일/날짜 그리드의 시작선(320 - 7×40 → 20px)에 맞춘다.
        root: { marginTop: 12, marginBottom: 4, paddingLeft: 20, paddingRight: 12 },
        label: { fontSize: FS.H2, fontWeight: 500, color: PANEL.ink },
        // 연/월 뷰 전환 화살표 — 기본 원형 호버 대신 색만 바뀐다(시안).
        switchViewButton: {
          color: PANEL.inkMuted,
          '&:hover': { color: PANEL.accentDeep, backgroundColor: 'transparent' },
        },
        switchViewIcon: { fontSize: 18 },
      },
    },
    // 이전/다음 달 화살표도 같은 문법(색만 반응).
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        button: {
          padding: 5,
          color: PANEL.iconIdle,
          '& .MuiSvgIcon-root': { fontSize: 19 },
          '&:hover': { color: PANEL.accentDeep, backgroundColor: 'transparent' },
          '&.Mui-disabled': { color: PANEL.iconFaint },
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        /*
         * 요일 머리글. 주말만 색으로 구분한다(시안).
         * ⚠ 첫/마지막 칸 = 일/토 라는 전제는 **주 시작이 일요일일 때만** 맞다.
         *   현재 앱은 AdapterDateFns 를 adapterLocale 없이 쓰므로 en-US(일요일 시작)로 고정이다.
         *   나중에 adapterLocale 을 주게 되면 이 두 줄을 요일 인덱스 기준으로 바꿔야 한다.
         */
        weekDayLabel: {
          fontSize: FS.CAPTION,
          fontWeight: 500,
          color: PANEL.inkLabel,
          '&:first-of-type': { color: PANEL.sun },
          '&:last-of-type': { color: PANEL.sat },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: FS.TITLE,
          fontWeight: 400,
          color: PANEL.inkBody,
          borderRadius: 10,   // 시안의 둥근 사각 알약(원형 → 사각)
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
          '&:focus': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
          '&.MuiPickersDay-today': {
            border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
          },
          '&.MuiPickersDay-dayOutsideMonth': { color: PANEL.iconFaint },
          '&.Mui-selected': {
            fontWeight: 500,
            backgroundColor: theme.palette.primary.main,
            // 시안의 글로우 — 선택 알약만 한 겹 떠 보이게 한다.
            boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.35)}`,
            '&:hover, &:focus': { backgroundColor: theme.palette.primary.dark },
          },
          '&.Mui-disabled:not(.Mui-selected)': { color: PANEL.iconFaint },
        }),
      },
    },
    /*
     * 월/연 그리드.
     *   MUI 기본은 72×36 알약(radius 18)이 320px 안에서 가운데 떠 있는 모양이라 열 간격이 들쭉날쭉하다.
     *   시안처럼 칸을 꽉 채우는 둥근 사각(radius 12)으로 바꾼다 — 폭은 칸(1/3)에서 좌우 5px 만 뺀다.
     *   글자도 MUI 가 subtitle1(=H1, 15/18px)을 펼쳐 넣어 날짜(13px)보다 커져 있던 것을 TITLE 로 맞춘다.
     */
    MuiPickersMonth: {
      styleOverrides: {
        monthButton: ({ theme }) => ({
          ...PICKER_CELL_SX(theme),
          margin: '5px 0',
        }),
      },
    },
    MuiPickersYear: {
      styleOverrides: {
        yearButton: ({ theme }) => ({
          ...PICKER_CELL_SX(theme),
          margin: '4px 0',
        }),
      },
    },
    MuiYearCalendar: {
      // 연 목록은 minDate~maxDate 만큼 길어져 스크롤이 생긴다 — Autocomplete 목록과 같은 얇은 바.
      styleOverrides: {
        root: ({ theme }) => ({
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.grey[300], borderRadius: 3 },
        }),
      },
    },
    /*
     * TimePicker 의 시/분 열.
     *   MUI 기본은 열 폭 56 = 항목 48 + 좌우 여백 4 로 **딱 맞고**, 스크롤바를 hover 일 때만 켠다.
     *   그래서 마우스를 올리는 순간 기본 스크롤바(약 15px)가 그 56 안으로 들어와 숫자를 잘라먹는다.
     *   → 바를 연 목록과 같은 6px 로 줄이고, 그만큼 열을 넓혀 자리를 따로 준다(48+8+6=62).
     *   → overflowY 를 항상 auto 로 둔다. hover 일 때만 켜면 그 순간 폭이 달라져 열이 흔들리고,
     *     무엇보다 **스크롤된다는 것 자체가 보이지 않는다**(시는 24칸이라 늘 잘려 있다).
     */
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: 62,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.grey[300], borderRadius: 3 },
        }),
      },
    },
  },
});

const persistor = persistStore(store);

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <ThemeProvider theme={THEME}>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <Styles />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <App/>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  </ThemeProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
